import _toConsumableArray from "/jove-dashboard/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { AmericanCountryList, EuropeanUnionList, PROVIDER_SOURCE, UKCountryList } from '../../constants/quote';
import { BusinessTypeNotSelectedOther, getGitInsuranceBundleList, getGreatAmericanInsuranceBundleList, getGreateAmericanBusinessTypeList } from '../../localeConstants';
import { getAverageRadiusOfOperationList, getGreateAmericanStateList, getGreateAmericanYearsOfExperienceList, getOtherTypeOfOperationList, getTypeOfOperationList } from '../../localeConstants/greateAmerican';
import { getQuoteRecruitmentBusinessTypeList } from '../../localeConstants/quotes/recruitmentQuote';
import { formatCurrencyNumber, getJoveCountryCurrencySymbolNative } from '../currency';
import { getLocalTranslation } from '../i18nKit';
import { getQuoteBusinessTypeList, getQuoteOtherBusinessTypeList } from '../quote';
import { getGreatAmericanTypeOfRemovalJovList } from './greatAmerican';
/**
 * Shared data sources for quote data operations.
 *
 * Collects reusable resolvers that derive dropdown option label/value pairs from
 * quote data, scoped by provider where needed. They mirror the lists used by the
 * quote form editors so a stored value always matches one of the returned entries,
 * and can be reused by any consumer (application form PDF builder, CMS editors,
 * etc.). Each resolver returns `{ options, optionValues }`: the PDF combo box shows
 * `options` (labels) and stores/transmits `optionValues` (the underlying codes),
 * while the Word .doc export only renders the labels.
 */

var hasLabel = function hasLabel(label) {
  return label !== undefined && label !== null && label !== '';
}; // Resolve dropdown labels at call time so the generated form reflects the language active when the
// options are built: the application form temporarily switches i18n to the quote's businessCountry
// language before generating the PDF / Word file, then restores the user's own language.


var $t = function $t(key) {
  return getLocalTranslation().$t(key);
};
/**
 * A dropdown's display labels plus the export values transmitted by the PDF field.
 * `options` and `optionValues` are parallel arrays (same order and length).
 */


/**
 * Build parallel label/value arrays from a `{ value, label }` list, dropping entries
 * without a label. An entry with no usable value falls back to its label so the two
 * arrays always stay aligned.
 */
var toDropdownOptions = function toDropdownOptions(list) {
  var items = (list || []).filter(function (item) {
    return hasLabel(item === null || item === void 0 ? void 0 : item.label);
  });
  return {
    options: items.map(function (item) {
      return item.label;
    }),
    optionValues: items.map(function (item) {
      return (item === null || item === void 0 ? void 0 : item.value) !== undefined && (item === null || item === void 0 ? void 0 : item.value) !== null && item.value !== '' ? String(item.value) : item.label;
    })
  };
};
/**
 * Resolve the shared Yes/No boolean dropdown options.
 *
 * Displays "Yes"/"No" while the PDF field stores/transmits "true"/"false". Reusable by any
 * boolean-style row (e.g. coverageConfiguration.requireInEU). The consuming row still sets its own
 * `content` to the "Yes"/"No" label so the display text and the Word .doc export stay label-based.
 */


export var getYesNoDropdownOptions = function getYesNoDropdownOptions() {
  return toDropdownOptions([{
    value: 'true',
    label: $t('text:yes')
  }, {
    value: 'false',
    label: $t('text:no')
  }]);
};
/**
 * Resolve the shared "I agree"/"I disagree" declaration dropdown options.
 *
 * Displays "I agree"/"I disagree" while the PDF field stores/transmits "true"/"false" (the
 * declaration answer's truelyValue). Mirrors getYesNoDropdownOptions for declaration questions; the
 * consuming row still sets its own `content` to the agree/disagree label so the display text and the
 * Word .doc export stay label-based.
 */

export var getAgreeDisagreeDropdownOptions = function getAgreeDisagreeDropdownOptions() {
  return toDropdownOptions([{
    value: 'true',
    label: $t('kitQuestion:i_agree')
  }, {
    value: 'false',
    label: $t('kitQuestion:i_disagree')
  }]);
};
/**
 * Resolve the declaration answer dropdown options for a question's confirmType.
 *
 * Declaration questions use a different label pair per confirmType, mirroring
 * getDeclarationsOptionsMap (localeConstants/quote.ts): '1' -> I agree/I disagree,
 * '2' -> Yes/No, '3' -> Include/Exclude. Every pair stores/transmits the answer's
 * truelyValue as 'true'/'false' with the positive label first, so only the labels
 * differ. Defaults to I agree/I disagree. Labels are resolved via i18n at call time.
 */

export var getDeclarationDropdownOptions = function getDeclarationDropdownOptions(confirmType) {
  if (confirmType === '2') {
    return getYesNoDropdownOptions();
  }

  if (confirmType === '3') {
    return toDropdownOptions([{
      value: 'true',
      label: $t('kit:application_form.include')
    }, {
      value: 'false',
      label: $t('kit:application_form.exclude')
    }]);
  }

  return getAgreeDisagreeDropdownOptions();
};
/**
 * Resolve the Great American state dropdown options.
 *
 * Mirrors getGreateAmericanStateList used by the personal details editor: the PDF
 * combo box displays the state name while storing/transmitting the state code
 * (e.g. "AL"). The consuming row sets its own `content` to the matching label.
 */

export var getStateDropdownOptions = function getStateDropdownOptions() {
  return toDropdownOptions(getGreateAmericanStateList());
};
/**
 * Resolve the country option labels for the given provider.
 *
 * Mirrors `canSelectCountryList` in the personal details editor: Great American
 * quotes can only pick the US, while Accelerant / GIT / Recruitment quotes are
 * limited to the UK and EU country lists. Options follow the canonical code list
 * order and are mapped to the localized labels carried on `countries`.
 */

export var getCountryDropdownOptions = function getCountryDropdownOptions(quoteDetails) {
  var _ref = quoteDetails || {},
      providerCode = _ref.providerCode,
      countries = _ref.countries;

  var isAmerican = [PROVIDER_SOURCE().GREATEAMERICAN.value].includes(providerCode);
  var codeList = isAmerican ? _toConsumableArray(AmericanCountryList) : [].concat(_toConsumableArray(UKCountryList), _toConsumableArray(EuropeanUnionList));
  var matched = codeList.map(function (code) {
    var _countries$find;

    return {
      value: code,
      label: countries === null || countries === void 0 ? void 0 : (_countries$find = countries.find(function (item) {
        return item.value === code;
      })) === null || _countries$find === void 0 ? void 0 : _countries$find.label
    };
  }).filter(function (item) {
    return hasLabel(item.label);
  });
  return {
    options: matched.map(function (item) {
      return item.label;
    }),
    optionValues: matched.map(function (item) {
      return item.value;
    })
  };
};
/**
 * Resolve the business type option labels for the given provider.
 *
 * Mirrors the business type editor (libs/uikit/src/quote/businessType/combine.tsx):
 * Great American uses its own fixed list, while the other providers combine the
 * main business type list with the "other" sub-list. The main list carries a
 * UI-only `Other` sentinel (BusinessTypeNotSelectedOther) that merely reveals the
 * sub-list when picked, so it is dropped here and the real "other" options are
 * appended, producing one flat list that matches the stored businessType value.
 * The country argument mirrors getBusinessTypeDisplayText so a stored value's
 * resolved label is always present among the returned options.
 */

export var getBusinessTypeDropdownOptions = function getBusinessTypeDropdownOptions(quoteDetails) {
  var _ref2 = quoteDetails || {},
      providerCode = _ref2.providerCode,
      businessCountry = _ref2.businessCountry;

  var list = [PROVIDER_SOURCE().GREATEAMERICAN.value].includes(providerCode) ? getGreateAmericanBusinessTypeList() : [].concat(_toConsumableArray(getQuoteBusinessTypeList(providerCode, {
    country: businessCountry
  }).filter(function (item) {
    return item.value !== BusinessTypeNotSelectedOther;
  })), _toConsumableArray(getQuoteOtherBusinessTypeList(providerCode, {
    country: businessCountry
  })));
  return toDropdownOptions(list);
};
/**
 * Resolve the business activity option labels.
 *
 * The caller supplies an already provider-resolved `businessActivityList`: the application form
 * quote-data hook builds it from the API for most providers and from getGitFixedBusinessActivities()
 * for Accelerant GIT, so no provider branch is needed here. Mirrors getBusinessActivityDisplayText
 * (text.ts), whose GIT branch reads that same fixed list, so the resolved content always matches an
 * option.
 */

export var getBusinessActivityDropdownOptions = function getBusinessActivityDropdownOptions(quoteDetails) {
  var _ref3 = quoteDetails || {},
      businessActivityList = _ref3.businessActivityList;

  return toDropdownOptions(businessActivityList || []);
};
/**
 * Resolve the insurance bundle option labels for the given provider.
 *
 * Mirrors getInsuranceBundleDisplayText (text.ts) and the insurance bundle editor
 * (libs/uikit/src/quote/insuranceBundle/combine.tsx): Great American Business
 * Insurance / Hiscox US use their own bundle list, everyone else uses the GIT
 * bundle list. This field has no "other" branch, so the list is returned as-is.
 */

export var getInsuranceBundleDropdownOptions = function getInsuranceBundleDropdownOptions(quoteDetails) {
  var _ref4 = quoteDetails || {},
      providerCode = _ref4.providerCode;

  var list = [PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value, PROVIDER_SOURCE().HISCOX_US.value].includes(providerCode) ? getGreatAmericanInsuranceBundleList() : getGitInsuranceBundleList();
  return toDropdownOptions(list);
};
/**
 * Resolve the recruitment business type option labels.
 *
 * Mirrors getRecruitmentBusinessTypeDisplayText (text.ts) and the recruitment
 * business type editor
 * (libs/uikit/src/quote/recruitment/recruitmentBusinessType/combine.tsx): a fixed
 * three-option list with no provider split and no "other" branch. The full-label
 * variant is used (not the short-text one) so the labels match the resolved
 * content.
 */

export var getRecruitmentBusinessTypeDropdownOptions = function getRecruitmentBusinessTypeDropdownOptions() {
  return toDropdownOptions(getQuoteRecruitmentBusinessTypeList());
};
/**
 * Resolve the type of removal job (Great American typeOfOperation) option labels.
 *
 * Mirrors getTypeOfRemovalJobDisplayText (text.ts) and the removal job editor
 * (libs/uikit/src/quote/greatAmerican/typeOfRemovalJob/combine.tsx): the main
 * type-of-operation list carries a UI-only `other` sentinel that reveals a second
 * dropdown, so it is dropped and the real "other" options are appended into one
 * flat list. isFastTrack stays false (same as the display resolver) so the full
 * list is offered and the resolved content always matches an option.
 */

export var getTypeOfRemovalJobDropdownOptions = function getTypeOfRemovalJobDropdownOptions(quoteDetails) {
  var _ref5 = quoteDetails || {},
      declarationQuestions = _ref5.declarationQuestions;

  var list = getTypeOfOperationList().filter(function (item) {
    return item.value !== 'other';
  });
  var displayList = getGreatAmericanTypeOfRemovalJovList([].concat(_toConsumableArray(list), _toConsumableArray(getOtherTypeOfOperationList())), {
    declarationQuestions: declarationQuestions,
    isFastTrack: false
  });
  return toDropdownOptions(displayList);
};
/**
 * Resolve the average radius of operation option labels (Great American).
 *
 * Mirrors getAverageRadiusOperationDisplayText (text.ts) and the average radius
 * editor (libs/uikit/src/quote/greatAmerican/averageRadiusOperation/combine.tsx):
 * a fixed mileage list with no provider split and no "other" branch. The same
 * getAverageRadiusOfOperationList() source as the display resolver is used so the
 * resolved content always matches an option.
 */

export var getAverageRadiusOperationDropdownOptions = function getAverageRadiusOperationDropdownOptions() {
  return toDropdownOptions(getAverageRadiusOfOperationList());
};
/**
 * Resolve the years in business (Great American years of experience) option labels.
 *
 * Mirrors getYearsInBusinessDisplayText (text.ts) and the years of experience
 * editor (libs/uikit/src/quote/greatAmerican/yearsOfExperience/combine.tsx): a
 * fixed list with no provider split and no "other" branch. The same
 * getGreateAmericanYearsOfExperienceList() getter as the display resolver is used
 * (the editor uses the equivalent constant) so content always matches an option.
 */

export var getYearsInBusinessDropdownOptions = function getYearsInBusinessDropdownOptions() {
  return toDropdownOptions(getGreateAmericanYearsOfExperienceList());
};
/**
 * Cover amounts per provider and cover id (hardcoded), where `0` means "I don't need this cover".
 *
 * The quote detail's `coverOptions` may only carry a partial set (the amounts currently available on
 * that quote), so the full selectable list of amounts is defined here per provider to guarantee the
 * dropdown always offers every option. Only the numeric amounts are stored: the display label is built
 * at resolve time from the currency symbol of the quote's businessCountry, since a provider can span
 * countries with different currencies (e.g. GIT/Accelerant cover both UK -> GBP and EU -> EUR).
 */

var getNoCoverLabel = function getNoCoverLabel() {
  return $t('text:dont_need_this_cover_option');
}; // A cover option seed is either a plain amount (its label is generated from the currency symbol) or
// an explicit { value, label } for covers whose label is a composite string (e.g. drivers negligence
// "5k Occ / 30k Agg"). An explicit label may use the {symbol} token where the currency symbol goes,
// so the symbol is still derived from the quote's businessCountry rather than hardcoded.


var GIT_COVER_VALUES_MAP = {
  'goods-in-transit': [10000, 20000, 30000, 40000, 50000],
  'public-liability': [0, 2000000, 5000000],
  'employers-liability': [0, 10000000],
  cmr: [0, 250000]
};
var ACCELERANT_COVER_VALUES_MAP = {
  'professional-indemnity': [100000, 300000, 500000, 1000000, 2000000, 5000000, 10000000],
  'excess-level': [250, 500, 1000],
  fidelity: [0, 10000, 25000, 50000, 100000, 250000, 500000, 1000000],
  'public-liability': [0, 1000000, 2000000, 5000000, 10000000],
  'employers-liability': [0, 10000000],
  'directors-officers': [0, 100000, 250000, 500000, 1000000, 2000000, 5000000],
  'employment-practices': [0, 100000, 250000, 500000, 1000000],
  'do-excess-level': [2500],
  'computer-breakdown': [25000],
  'money-assault': [15000]
};
var RECRUITMENT_COVER_VALUES_MAP = {
  'professional-indemnity': [250000, 500000, 1000000, 2000000, 5000000, 10000000],
  'excess-level': [250, 500, 1000, 2500],
  fidelity: [10000, 25000, 50000, 100000, 250000, 500000, 1000000],
  'public-liability': [1000000, 2000000, 5000000, 10000000],
  'employers-liability': [10000000],
  'employment-practices': [100000, 250000, 500000, 1000000],
  'drivers-neg': [{
    value: 30000,
    label: '{symbol}5k Occ / {symbol}30k Agg'
  }, {
    value: 40000,
    label: '{symbol}10k Occ / {symbol}40k Agg'
  }, {
    value: 50000,
    label: '{symbol}15k Occ / {symbol}50k Agg'
  }, {
    value: 60000,
    label: '{symbol}20k Occ / {symbol}60k Agg'
  }],
  'directors-officers': [100000, 250000, 500000, 1000000, 2000000, 5000000],
  'do-excess-level': [2500],
  'computer-breakdown': [25000],
  'money-assault': [15000]
}; // Provider -> hardcoded cover amount map. Providers without one fall back to the quote's own options.

var getProviderCoverValuesMap = function getProviderCoverValuesMap(providerCode) {
  if ([PROVIDER_SOURCE().ACCELERANT_GIT.value].includes(providerCode)) {
    return GIT_COVER_VALUES_MAP;
  }

  if ([PROVIDER_SOURCE().ACCELERANT.value].includes(providerCode)) {
    return ACCELERANT_COVER_VALUES_MAP;
  }

  if ([PROVIDER_SOURCE().RECRUITMENT.value].includes(providerCode)) {
    return RECRUITMENT_COVER_VALUES_MAP;
  }

  return undefined;
};
/**
 * Resolve the complete cover option list for a single cover of the quote's provider.
 *
 * Prefers the hardcoded per-provider amounts so the full selectable set is always available, labelling
 * each with the currency symbol for the quote's businessCountry; falls back to the quote's fetched
 * `coverOptions[key]` for providers/covers without a hardcoded list. The same list feeds both the row
 * content (getCoverFieldContent) and the dropdown options, so the selected label always matches an option.
 */


export var getCoverOptionList = function getCoverOptionList(quoteDetails, key) {
  var _getProviderCoverValu, _quoteDetails$coverOp;

  var _ref6 = quoteDetails || {},
      providerCode = _ref6.providerCode,
      businessCountry = _ref6.businessCountry,
      countries = _ref6.countries;

  var seeds = (_getProviderCoverValu = getProviderCoverValuesMap(providerCode)) === null || _getProviderCoverValu === void 0 ? void 0 : _getProviderCoverValu[key];

  if (seeds !== null && seeds !== void 0 && seeds.length) {
    var currencySymbol = getJoveCountryCurrencySymbolNative({
      country: businessCountry,
      countries: countries
    });
    return seeds.map(function (seed) {
      // Plain amounts get the standard currency-formatted label (0 -> "I don't need this cover");
      // explicit { value, label } seeds keep their composite label with the {symbol} token resolved.
      if (typeof seed === 'number') {
        return {
          value: seed,
          label: seed === 0 ? getNoCoverLabel() : "".concat(currencySymbol).concat(formatCurrencyNumber(seed))
        };
      }

      return {
        value: seed.value,
        label: seed.label.replace(/\{symbol\}/g, currencySymbol)
      };
    });
  }

  return (quoteDetails === null || quoteDetails === void 0 ? void 0 : (_quoteDetails$coverOp = quoteDetails.coverOptions) === null || _quoteDetails$coverOp === void 0 ? void 0 : _quoteDetails$coverOp[key]) || [];
};
/**
 * Resolve the dropdown options for a single cover of the quote's provider.
 *
 * Displays the cover label while the PDF field stores/transmits the numeric cover value. Sources
 * from getCoverOptionList (hardcoded per provider), so it is complete even when the quote detail's
 * coverOptions is partial. Returns empty options for boolean (Yes/No) covers, which have no list.
 */

export var getCoverDropdownOptions = function getCoverDropdownOptions(quoteDetails, key) {
  return toDropdownOptions(getCoverOptionList(quoteDetails, key));
};